import { useState, useCallback } from 'react';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { getUserData, linkGoogleCalendar, linkGoogleAccount, canLinkGoogleAccount } from '../firebase';
import { db, auth } from '../firebase';
import { User } from '../types';

export function useGoogleIntegration(
  user: User | null,
  refreshUserData: (userId: string) => Promise<void>,
  showAlert: (message: string) => void,
  showConfirm: (message: string, onConfirm: () => void | Promise<void>) => void
) {
  const [isLinkingGoogle, setIsLinkingGoogle] = useState(false);
  const [isLinkingCalendar, setIsLinkingCalendar] = useState(false);

  const handleLinkGoogleAccount = useCallback(async () => {
    if (!user || !auth.currentUser) return;
    
    setIsLinkingGoogle(true);
    try {
      // Get current user data from Firestore
      const userData = await getUserData(user.id);
      const canLink = await canLinkGoogleAccount(auth.currentUser, userData);
      if (!canLink) {
        console.error('Google account already linked');
        return;
      }
      
      await linkGoogleAccount(auth.currentUser);
      await refreshUserData(user.id);
      // Google account linked successfully
    } catch (error: any) {
      console.error('Error linking Google account:', error);
      const errorMessage = error?.message || 'Failed to link Google account. Please try again.';
      console.error('Error:', errorMessage);
    } finally {
      setIsLinkingGoogle(false);
    }
  }, [user, refreshUserData]);

  const handleLinkGoogleCalendar = useCallback(async () => {
    if (!user || !auth.currentUser) return;
    
    setIsLinkingCalendar(true);
    try {
      const result = await linkGoogleCalendar(auth.currentUser) as { success: boolean; message?: string };
      if (result.success) {
        await refreshUserData(user.id);
        // Google Calendar linked successfully
      } else {
        console.error('Failed to link Google Calendar:', result.message);
      }
    } catch (error: any) {
      console.error('Error linking Google Calendar:', error);
      const errorMessage = error?.message || 'Failed to link Google Calendar. Please try again.';
      console.error('Error:', errorMessage);
    } finally {
      setIsLinkingCalendar(false);
    }
  }, [user, refreshUserData]);

  const handleUnlinkGoogleCalendar = useCallback(async () => {
    if (!user || !auth.currentUser) return;
    
    showConfirm('Are you sure you want to unlink your Google Calendar?', async () => {
      setIsLinkingCalendar(true);
      try {
        // Update user document to remove Google Calendar link
        const userRef = doc(db, 'users', user.id);
        await updateDoc(userRef, {
          hasGoogleCalendar: false,
          googleCalendarToken: null,
          googleCalendarUnlinkedAt: serverTimestamp()
        });
        
        await refreshUserData(user.id);
        showAlert('Google Calendar unlinked successfully!');
      } catch (error: any) {
        console.error('Error unlinking Google Calendar:', error);
        const errorMessage = error?.message || 'Failed to unlink Google Calendar. Please try again.';
        showAlert(errorMessage);
      } finally {
        setIsLinkingCalendar(false);
      }
    });
  }, [user, refreshUserData, showAlert, showConfirm]);

  return {
    isLinkingGoogle,
    isLinkingCalendar,
    handleLinkGoogleAccount,
    handleLinkGoogleCalendar,
    handleUnlinkGoogleCalendar
  };
}


